import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ShieldAlert, RefreshCw, Loader2, CheckCircle2 } from 'lucide-react';
import api from '../lib/api';
import { OnlineOnlyNotice } from '../components/OnlineOnlyNotice';

const ageTone = (d) => (d >= 7 ? 'bg-red-100 text-red-800' : d >= 3 ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700');
const dayLabel = (d) => (d == null ? 'Unknown' : d === 0 ? 'Today' : `${d} day${d === 1 ? '' : 's'}`);

const BranchCard = ({ br }) => (
  <Card data-testid={`recall-report-branch-${br.tenant_id}`}>
    <CardHeader className="pb-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <CardTitle className="text-base">{br.branch_name}</CardTitle>
        <div className="flex gap-2 text-xs">
          <Badge variant="destructive" data-testid={`recall-report-units-${br.tenant_id}`}>{br.units} units held</Badge>
          <Badge className={ageTone(br.oldest_days)} data-testid={`recall-report-oldest-${br.tenant_id}`}>Oldest: {dayLabel(br.oldest_days)}</Badge>
          <Badge variant="outline" data-testid={`recall-report-unpulled-${br.tenant_id}`}>{br.unpulled} not pulled</Badge>
        </div>
      </div>
    </CardHeader>
    <CardContent className="overflow-x-auto">
      <Table>
        <TableHeader><TableRow>
          <TableHead>Medicine</TableHead><TableHead>Batch</TableHead><TableHead className="text-right">Units</TableHead>
          <TableHead>On shelf for</TableHead><TableHead>Shelf status</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {br.rows.map((r) => (
            <TableRow key={`${r.product_id}-${r.batch_number}`} data-testid={`recall-report-row-${br.tenant_id}-${r.product_id}`}>
              <TableCell className="font-medium">{r.name}</TableCell>
              <TableCell>{r.batch_number || 'All batches'}</TableCell>
              <TableCell className="text-right tabular-nums">{r.units}</TableCell>
              <TableCell><Badge className={ageTone(r.days_on_shelf || 0)}>{dayLabel(r.days_on_shelf)}</Badge>
                {r.recalled_at && <span className="block text-xs text-muted-foreground">since {new Date(r.recalled_at).toLocaleDateString()}</span>}</TableCell>
              <TableCell className="text-sm">{r.pulled
                ? <span className="text-emerald-700">Pulled by {r.pulled.pulled_by_name} · {new Date(r.pulled.pulled_at).toLocaleString()}</span>
                : <span className="text-red-700 font-medium">Still on the shelf</span>}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

export default function BranchRecallReportPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData((await api.get('/recall-returns/branch-report')).data); setError(''); } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load the report');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="branch-recall-report-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><ShieldAlert className="h-7 w-7 text-red-600" />Branch Recall Report</h1>
          <p className="text-muted-foreground text-sm mt-1">Which of your branches still hold recalled stock, and for how long. Oldest first.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} className="gap-1.5" data-testid="recall-report-refresh">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
        </Button>
      </div>
      <OnlineOnlyNotice feature="The branch recall report" testId="recall-report-offline-notice" />
      {error && <p className="text-sm text-red-700" data-testid="recall-report-error">{error}</p>}
      {data && (
        <>
          <p className="text-sm" data-testid="recall-report-summary">
            <strong>{data.total_units}</strong> recalled unit{data.total_units === 1 ? '' : 's'} across <strong>{data.branches.length}</strong> branch{data.branches.length === 1 ? '' : 'es'}.
            {' '}Returns are logged on each medicine's page (<Link to="/inventory" className="underline">Inventory</Link>).
          </p>
          {data.branches.map((br) => <BranchCard key={br.tenant_id} br={br} />)}
          {data.clear_branches.length > 0 && (
            <Card data-testid="recall-report-clear"><CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" />No recalled stock</CardTitle>
              <CardDescription>{data.clear_branches.map((b) => b.branch_name).join(', ')}</CardDescription></CardHeader></Card>
          )}
        </>
      )}
    </div>
  );
}
